// src/services/incidentEventService.js

import { db } from "../firebase";
import {
  addDoc,
  collection,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";

/**
 * Append-only operational record for an incident: problems/{problemId}/events.
 *
 * Every meaningful action writes exactly one event. This single collection is
 * the source for the workspace timeline, the customer-facing audit trail, and
 * the resolution-intelligence export, so nothing downstream has to reconstruct
 * "what actually happened" by scraping chat messages after the fact.
 *
 * Events are never updated or deleted; firestore.rules enforces that.
 */

export const EVENT_CATEGORY = {
  LIFECYCLE: "lifecycle",
  COMMS: "comms",
  SESSION: "session",
  EVIDENCE: "evidence",
  INVESTIGATION: "investigation",
  RESOLUTION: "resolution",
};

/**
 * Who is allowed to see an event in the timeline.
 *
 * SHARED events are part of the record both parties see. INTERNAL events are
 * the expert's own working notes — recorded for the intelligence database and
 * the audit trail, but not surfaced to the customer.
 */
export const EVENT_VISIBILITY = {
  SHARED: "shared",
  INTERNAL: "internal",
};

/**
 * Event type registry. `id` values are persisted in Firestore and must stay
 * stable; label and icon are presentation only and safe to change.
 * Icon names refer to lucide-react exports.
 */
export const INCIDENT_EVENTS = {
  INCIDENT_REPORTED: {
    id: "incident.reported",
    category: EVENT_CATEGORY.LIFECYCLE,
    label: "Incident reported",
    icon: "FileWarning",
  },
  INCIDENT_PAID: {
    id: "incident.paid",
    category: EVENT_CATEGORY.LIFECYCLE,
    label: "Session confirmed",
    icon: "CreditCard",
  },
  EXPERT_ASSIGNED: {
    id: "incident.assigned",
    category: EVENT_CATEGORY.LIFECYCLE,
    label: "Expert assigned",
    icon: "UserCheck",
  },
  WORK_STARTED: {
    id: "incident.work_started",
    category: EVENT_CATEGORY.LIFECYCLE,
    label: "Investigation started",
    icon: "PlayCircle",
  },
  FIRST_RESPONSE: {
    id: "incident.first_response",
    category: EVENT_CATEGORY.COMMS,
    label: "First expert response",
    icon: "MessageSquareReply",
  },
  SESSION_STARTED: {
    id: "session.started",
    category: EVENT_CATEGORY.SESSION,
    label: "Live session started",
    icon: "Video",
  },
  SESSION_ENDED: {
    id: "session.ended",
    category: EVENT_CATEGORY.SESSION,
    label: "Live session ended",
    icon: "VideoOff",
  },
  SCREEN_SHARE_STARTED: {
    id: "session.screen_share_started",
    category: EVENT_CATEGORY.SESSION,
    label: "Screen sharing started",
    icon: "MonitorUp",
  },
  EVIDENCE_ADDED: {
    id: "evidence.added",
    category: EVENT_CATEGORY.EVIDENCE,
    label: "Evidence added",
    icon: "Paperclip",
  },
  EVIDENCE_REMOVED: {
    id: "evidence.removed",
    category: EVENT_CATEGORY.EVIDENCE,
    label: "Evidence removed",
    icon: "Trash2",
  },
  CONTEXT_UPDATED: {
    id: "investigation.context_updated",
    category: EVENT_CATEGORY.INVESTIGATION,
    label: "Incident context updated",
    icon: "ClipboardList",
  },
  FINDING_RECORDED: {
    id: "investigation.finding_recorded",
    category: EVENT_CATEGORY.INVESTIGATION,
    label: "Finding recorded",
    icon: "Microscope",
  },
  RUNBOOK_STEP_COMPLETED: {
    id: "investigation.runbook_step_completed",
    category: EVENT_CATEGORY.INVESTIGATION,
    label: "Diagnostic step completed",
    icon: "ListChecks",
  },
  REPORT_FILED: {
    id: "resolution.report_filed",
    category: EVENT_CATEGORY.RESOLUTION,
    label: "Resolution report filed",
    icon: "ShieldCheck",
  },
  RESOLUTION_CONFIRMED: {
    id: "resolution.confirmed_by_party",
    category: EVENT_CATEGORY.RESOLUTION,
    label: "Resolution confirmed",
    icon: "CheckCircle2",
  },
  INCIDENT_RESOLVED: {
    id: "resolution.resolved",
    category: EVENT_CATEGORY.RESOLUTION,
    label: "Incident resolved",
    icon: "CheckCheck",
  },
  INCIDENT_UNRESOLVED: {
    id: "resolution.unresolved",
    category: EVENT_CATEGORY.RESOLUTION,
    label: "Closed as unresolved",
    icon: "AlertTriangle",
  },
  REFUND_REQUESTED: {
    id: "resolution.refund_requested",
    category: EVENT_CATEGORY.RESOLUTION,
    label: "Refund requested",
    icon: "Undo2",
  },
  INCIDENT_REOPENED: {
    id: "lifecycle.reopened",
    category: EVENT_CATEGORY.LIFECYCLE,
    label: "Incident reopened",
    icon: "RotateCcw",
  },
};

const EVENT_BY_ID = new Map(
  Object.values(INCIDENT_EVENTS).map((event) => [event.id, event])
);

const UNKNOWN_EVENT = {
  id: "unknown",
  category: EVENT_CATEGORY.LIFECYCLE,
  label: "Activity",
  icon: "Circle",
};

/**
 * Appends an event to the incident record.
 *
 * This never throws. Timeline writes are secondary to the action that triggered
 * them, so a failed event must never roll back or block the real work — it is
 * logged and reported in the return value instead.
 *
 * @param {string} problemId
 * @param {object} options
 * @param {object|string} options.type      Entry from INCIDENT_EVENTS, or its id.
 * @param {object} [options.actor]          `{ id, name, role }` of who acted.
 * @param {string} [options.summary]        One-line human description.
 * @param {string} [options.detail]         Optional longer body.
 * @param {object} [options.metadata]       Structured payload for later analysis.
 * @param {string} [options.visibility]     EVENT_VISIBILITY value.
 */
export async function recordIncidentEvent(problemId, options = {}) {
  const { type, actor, summary, detail, metadata, visibility } = options;
  const typeId = typeof type === "string" ? type : type?.id;

  if (!problemId || !typeId) {
    console.warn("recordIncidentEvent called without a problemId or type");
    return { success: false, error: "problemId and type are required" };
  }

  try {
    const docRef = await addDoc(
      collection(db, "problems", problemId, "events"),
      {
        type: typeId,
        category: EVENT_BY_ID.get(typeId)?.category || UNKNOWN_EVENT.category,
        visibility: visibility || EVENT_VISIBILITY.SHARED,
        actorId: actor?.id || null,
        actorName: actor?.name || null,
        actorRole: actor?.role || null,
        summary: summary || null,
        detail: detail || null,
        metadata: metadata || null,
        createdAt: serverTimestamp(),
      }
    );
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error(`Could not record incident event "${typeId}":`, error);
    return { success: false, error: error.message };
  }
}

/**
 * Subscribes to the incident timeline, oldest first.
 *
 * @returns {() => void} unsubscribe
 */
export function subscribeToIncidentEvents(problemId, callback, options = {}) {
  if (!problemId) return () => {};

  const constraints = [orderBy("createdAt", "asc")];
  if (options.max) constraints.push(fbLimit(options.max));

  const eventsQuery = query(
    collection(db, "problems", problemId, "events"),
    ...constraints
  );

  return onSnapshot(
    eventsQuery,
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (error) => {
      console.error("Incident events subscription error:", error);
      callback([]);
    }
  );
}

/** Presentation metadata for an event document. */
export function describeEvent(event) {
  const definition = EVENT_BY_ID.get(event?.type) || UNKNOWN_EVENT;
  return {
    ...definition,
    summary: event?.summary || definition.label,
  };
}

const toMillis = (timestamp) =>
  timestamp?.seconds ? timestamp.seconds * 1000 : null;

/**
 * Derives the operational shape of an incident from its event stream: when each
 * milestone happened, and how much live session time was spent.
 *
 * Reading these from events rather than problem fields means the numbers cannot
 * drift out of sync with the record they are supposed to describe.
 */
export function summarizeIncidentEvents(events = []) {
  const firstOf = (typeId) =>
    toMillis(events.find((e) => e.type === typeId)?.createdAt);

  let sessionCount = 0;
  let sessionMs = 0;
  let openSessionStart = null;

  for (const event of events) {
    if (event.type === INCIDENT_EVENTS.SESSION_STARTED.id) {
      sessionCount += 1;
      openSessionStart = toMillis(event.createdAt);
    } else if (event.type === INCIDENT_EVENTS.SESSION_ENDED.id) {
      const endedAt = toMillis(event.createdAt);
      if (openSessionStart && endedAt && endedAt > openSessionStart) {
        sessionMs += endedAt - openSessionStart;
      }
      openSessionStart = null;
    }
  }

  return {
    reportedAt: firstOf(INCIDENT_EVENTS.INCIDENT_REPORTED.id),
    assignedAt: firstOf(INCIDENT_EVENTS.EXPERT_ASSIGNED.id),
    firstResponseAt: firstOf(INCIDENT_EVENTS.FIRST_RESPONSE.id),
    workStartedAt: firstOf(INCIDENT_EVENTS.WORK_STARTED.id),
    resolvedAt: firstOf(INCIDENT_EVENTS.INCIDENT_RESOLVED.id),
    sessionCount,
    sessionMs,
    /** True while a session is still open, so callers can show a live counter. */
    sessionInProgress: openSessionStart !== null,
  };
}
